<template>
  <DashboardLayout :pin-profile-sidebar="!lobby?.lan_party">
    <div v-if="loading" class="max-w-7xl mx-auto text-text-secondary">{{ $t('play.matchmaking.loading_lobby') }}</div>

    <div v-else-if="error" class="max-w-7xl mx-auto flex flex-col items-start gap-4">
      <p class="text-red-400">{{ error }}</p>
      <router-link to="/matchmaking" class="flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gray-800 text-text-primary text-base font-semibold hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
        <ArrowLeftIcon class="w-4 h-4" aria-hidden="true" />
        {{ $t('play.matchmaking.back_to_matchmaking') }}
      </router-link>
    </div>

    <div v-else-if="lobby" class="max-w-7xl mx-auto flex flex-col gap-10">
      <LanLobbyBar v-if="lobby.lan_party" :lobby="lobby" />
      <RequeueBar v-else-if="lobby.status === 'completed' || lobby.status === 'cancelled'" />

      <section class="relative">
        <img
          v-if="mapArt && !mapArtFailed"
          :src="mapArt"
          alt=""
          class="map-art absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
          @error="mapArtFailed = true"
        />
        <div class="relative px-6 lg:px-8 pt-6 pb-10">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div class="flex items-center gap-2 min-w-0">
              <img :src="refragLogo" alt="" class="w-5 h-5 rounded-full object-cover select-none" />
              <span class="text-text-secondary">{{ lobby.play_mode?.friendly_name }}</span>
              <template v-if="lobby.cs_map">
                <span class="text-text-disabled">•</span>
                <img :src="`/maps/map_icon_${lobby.cs_map.name}.svg`" alt="" class="w-5 h-5 select-none" />
                <span class="text-text-secondary">{{ lobby.cs_map.friendly_name }}</span>
              </template>
            </div>

            <div class="flex flex-col items-center gap-3 text-center lg:pt-4">
              <span class="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold" :class="statusChip.class">
                <span v-if="statusChip.pulse" class="relative flex w-2 h-2" aria-hidden="true">
                  <span class="absolute inline-flex w-full h-full rounded-full bg-current opacity-75 motion-safe:animate-ping" />
                  <span class="relative inline-flex w-2 h-2 rounded-full bg-current" />
                </span>
                {{ statusChip.label }}
              </span>

              <div
                v-if="(lobby.status === 'completed' || lobby.status === 'active') && hasScores && teams.length === 2"
                class="text-5xl lg:text-6xl font-bold tabular-nums text-text-primary flex items-center gap-3"
              >
                <span :class="scoreColor(0)">{{ teamScore(0)?.score ?? 0 }}</span>
                <span class="text-gray-600">-</span>
                <span :class="scoreColor(1)">{{ teamScore(1)?.score ?? 0 }}</span>
              </div>
              <div v-else-if="showPodium && myPlacing" class="flex items-baseline gap-2">
                <span class="text-5xl lg:text-6xl font-bold tabular-nums" :class="placeText(myPlacing.place)">#{{ myPlacing.rank }}</span>
                <span class="text-lg text-text-secondary">{{ $t('play.matchmaking.placement_of', { n: placings.length }) }}</span>
              </div>
              <div
                v-else-if="connectOpen"
                class="text-5xl lg:text-6xl font-bold tabular-nums"
                :class="connectUrgent ? 'text-red-400' : 'text-text-primary'"
              >{{ connectLabel }}</div>
              <div v-else-if="lobby.status === 'active' && teams.length === 2" class="flex items-center gap-5">
                <div v-for="(team, index) in teams" :key="index" class="flex -space-x-3" :class="{ 'order-3': index === 1 }">
                  <template v-for="member in team" :key="member.user?.uuid">
                    <img
                      v-if="memberAvatar(member)"
                      :src="memberAvatar(member)"
                      alt=""
                      class="w-14 h-14 rounded-full object-cover ring-4 ring-gray-950"
                    />
                  </template>
                </div>
                <span class="order-2 text-2xl font-bold text-text-secondary">{{ $t('play.matchmaking.versus') }}</span>
              </div>
              <svg
                v-else-if="statusInProgress"
                class="w-12 h-12 text-primary-400 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>

              <p v-if="heroCaption" class="text-sm text-text-secondary">{{ heroCaption }}</p>
            </div>

            <div class="flex items-center gap-2 lg:justify-end">
              <template v-if="lobby.status === 'completed'">
                <router-link
                  v-if="lobby.match?.uuid"
                  :to="{ name: 'MatchPage', params: { id: lobby.match.uuid } }"
                  class="flex-shrink-0 px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold shadow-[0_0_20px_rgba(78,84,200,0.3)] hover:bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
                >
                  {{ $t('play.matchmaking.view_match') }}
                </router-link>
                <span
                  v-else
                  class="flex-shrink-0 px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold opacity-50 cursor-not-allowed"
                  :title="$t('play.matchmaking.stats_not_ready')"
                >{{ $t('play.matchmaking.view_match') }}</span>
              </template>
              <template v-else-if="lobby.cs_server && serverLive">
                <button
                  v-if="inSteamClient"
                  type="button"
                  class="flex-shrink-0 px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold shadow-[0_0_20px_rgba(78,84,200,0.3)] hover:bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
                  :title="$t('play.matchmaking.copy_server_info_title')"
                  @click="copyConnectCommand"
                >
                  {{ copied ? $t('play.matchmaking.copied') : $t('play.matchmaking.copy_server_info') }}
                </button>
                <a
                  v-else
                  class="text-center flex-shrink-0 px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold shadow-[0_0_20px_rgba(78,84,200,0.3)] hover:bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
                  :href="steamConnectUrl"
                  @click.stop
                >
                  {{ $t('play.matchmaking.join_server') }}
                </a>
              </template>
            </div>
          </div>
        </div>
      </section>

      <div v-if="lobby.cs_server && serverLive" class="flex flex-col gap-2 min-w-0">
        <p class="text-sm font-semibold text-text-secondary">{{ $t('play.matchmaking.server') }}</p>
        <div class="flex items-center gap-3 min-w-0">
          <code class="font-mono text-xl sm:text-2xl font-bold text-text-primary tracking-tight truncate">
            connect {{ lobby.cs_server.ip }}:{{ lobby.cs_server.port }}<span
              v-if="lobby.cs_server.password"
              :class="{ 'blur-sm': user.streamer_mode }"
            >; password {{ lobby.cs_server.password }}</span>
          </code>
          <button
            @click="copyConnectCommand"
            :title="$t('play.shared.copy_connect_command')"
            :aria-label="$t('play.shared.copy_connect_command')"
            class="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <img v-if="!copied" src="../assets/icons/clipboard.svg" class="w-5 h-5" alt="" />
            <CheckCircleIcon v-else class="w-5 h-5 text-green-400" />
          </button>
        </div>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-300">
          <span v-if="lobby.server_location" class="inline-flex items-center gap-2">
            <flag v-if="lobby.server_location.flag" :rounded="true" :iso="lobby.server_location.flag" class="rounded-full h-4 w-4" />
            {{ lobby.server_location.name }}
          </span>
        </div>
      </div>
      <ol
        v-if="showPodium"
        class="grid grid-cols-2 sm:grid-cols-[repeat(4,minmax(0,13rem))] gap-4"
        :class="widePlacings ? 'xl:grid-cols-[repeat(var(--seats),minmax(0,1fr))] xl:gap-3' : ''"
        :style="{ '--seats': placings.length }"
      >
        <li
          v-for="row in placings"
          :key="row.index"
          class="relative h-56 rounded-2xl flex flex-col items-center justify-center gap-4 px-3 text-center"
          :class="[
            row.place <= 3 ? `podium-${row.place}` : 'podium-rest',
            { 'opacity-60': myTeamIndex !== -1 && row.index !== myTeamIndex },
          ]"
        >
          <span class="absolute top-3 left-4 text-xl font-bold tabular-nums" :class="placeText(row.place)">{{ row.rank }}</span>
          <div class="flex -space-x-4">
            <template v-for="member in teams[row.index]" :key="member.user?.uuid">
              <img
                v-if="memberAvatar(member)"
                :src="memberAvatar(member)"
                alt=""
                class="rounded-full object-cover ring-2"
                :class="[placingAvatarSize, placeRing(row.place)]"
              />
            </template>
          </div>
          <div class="w-full min-w-0">
            <p class="text-base font-semibold text-text-primary truncate">{{ row.label }}</p>
            <p class="text-3xl font-bold tabular-nums text-text-primary mt-1">{{ row.score }}</p>
          </div>
        </li>
      </ol>

      <template v-if="!showPodium">
        <section v-if="teams.length === 2" class="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch">
          <template v-for="(team, index) in teams" :key="index">
            <div
              v-if="index === 1"
              class="hidden lg:flex flex-col items-center justify-center gap-2 text-text-disabled"
            >
              <span class="w-px flex-1 bg-gray-800" />
              <span class="text-lg font-bold text-text-secondary">{{ $t('play.matchmaking.versus') }}</span>
              <span class="w-px flex-1 bg-gray-800" />
            </div>
            <div
              class="flex flex-col gap-3 rounded-2xl px-5 py-4 min-w-0"
              :class="index === 1 ? 'faceoff-away lg:items-end' : 'faceoff-home'"
            >
              <div class="flex items-center gap-2.5" :class="{ 'lg:flex-row-reverse': mirrored(index) }">
                <span class="w-2 h-2 rounded-full" :class="teamAccent(index).dot" aria-hidden="true" />
                <h2 class="text-sm font-semibold text-text-secondary">{{ $t('play.matchmaking.team', { n: index + 1 }) }}</h2>
              </div>
              <ul class="flex flex-col gap-1 w-full">
                <li
                  v-for="member in team"
                  :key="member.user?.uuid"
                  class="flex items-center gap-3 py-1.5 text-base"
                  :class="{ 'lg:flex-row-reverse lg:text-right': mirrored(index) }"
                >
                  <img
                    v-if="memberAvatar(member)"
                    :src="memberAvatar(member)"
                    alt=""
                    class="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    :class="{ 'ring-2 ring-green-400': member.user?.uuid === user?.uuid }"
                  />
                  <span class="flex-1 min-w-0 flex" :class="{ 'lg:justify-end': mirrored(index) }"><span
                    class="min-w-0 truncate"
                    :class="member.user?.uuid === user?.uuid ? 'font-semibold text-text-primary' : 'text-gray-300'"
                  >{{ member.user?.uuid === user?.uuid ? $t('play.lan_party.you_named', { name: memberName(member) }) : memberName(member) }}</span></span>
                  <span
                    v-if="serverLive"
                    class="flex-shrink-0 inline-flex items-center gap-2 text-xs"
                    :class="[
                      member.connected ? 'text-teal-400' : 'text-text-secondary',
                      { 'lg:flex-row-reverse': mirrored(index) },
                    ]"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="member.connected ? 'bg-teal-400' : 'bg-gray-600'"
                      aria-hidden="true"
                    />
                    {{ member.connected ? $t('play.matchmaking.connected') : $t('play.matchmaking.not_connected') }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </section>
        <ul
          v-else
          class="grid grid-cols-2 sm:grid-cols-[repeat(4,minmax(0,13rem))] gap-4"
          :class="teams.length > 4 ? 'xl:grid-cols-[repeat(var(--seats),minmax(0,1fr))] xl:gap-3' : ''"
          :style="{ '--seats': teams.length }"
        >
          <li
            v-for="(team, index) in teams"
            :key="index"
            class="relative h-56 rounded-2xl flex flex-col items-center justify-center gap-4 px-3 text-center"
            :class="index === myTeamIndex ? 'seat-mine ring-1 ring-primary-700' : 'podium-rest'"
          >
            <div class="flex -space-x-4">
              <template v-for="member in team" :key="member.user?.uuid">
                <img
                  v-if="memberAvatar(member)"
                  :src="memberAvatar(member)"
                  alt=""
                  class="rounded-full object-cover ring-2 ring-gray-700"
                  :class="teams.length > 4 ? 'w-20 h-20 xl:w-16 xl:h-16' : 'w-20 h-20'"
                />
              </template>
            </div>
            <div class="w-full min-w-0">
              <p class="text-base font-semibold text-text-primary truncate">
                {{ team.length === 1 ? memberName(team[0]) : $t('play.matchmaking.team', { n: index + 1 }) }}
              </p>
              <p v-if="hasScores" class="text-3xl font-bold tabular-nums text-text-primary mt-1">{{ teamScore(index)?.score ?? 0 }}</p>
              <p v-if="serverLive" class="mt-1 inline-flex items-center gap-1.5 text-xs text-text-secondary">
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="team.every((m) => m.connected) ? 'bg-teal-400' : 'bg-gray-600'"
                  aria-hidden="true"
                />
                {{ team.every((m) => m.connected) ? $t('play.matchmaking.connected') : $t('play.matchmaking.not_connected') }}
              </p>
            </div>
          </li>
        </ul>
      </template>

      <section class="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div class="relative h-[250px] rounded-xl border border-gray-800 overflow-hidden bg-gray-900">
          <div
            v-if="playlistImage"
            class="absolute inset-0"
            :style="`background-image: url('${playlistImage}'); background-size: cover; background-position: center;`"
          />
          <div class="absolute inset-0 card-gradient" />
          <p class="relative z-10 text-xs uppercase tracking-wide text-gray-300 px-6 pt-4">{{ $t('play.matchmaking.playlist') }}</p>
          <p class="absolute bottom-4 left-6 z-10 text-xl font-bold text-white">{{ lobby.play_mode?.friendly_name }}</p>
        </div>

        <div class="relative h-[250px] rounded-xl border border-gray-800 overflow-hidden bg-gray-900">
          <img src="../assets/dust2-outline.svg" class="absolute inset-0 w-full h-full object-cover" alt="" />
          <p class="relative z-10 text-xs uppercase tracking-wide text-gray-300 px-6 pt-4">{{ $t('common.map') }}</p>
          <img
            v-if="lobby.cs_map && !mapPinFailed"
            :src="`/maps/${lobby.cs_map.name.toLowerCase()}_pin.png`"
            alt=""
            class="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-28 h-28 object-contain drop-shadow-lg"
            @error="mapPinFailed = true"
          />
          <p class="absolute bottom-4 left-6 z-10 text-xl font-bold text-white">{{ lobby.cs_map?.friendly_name || $t('play.matchmaking.map_tbd') }}</p>
        </div>

        <div class="relative h-[250px] rounded-xl border border-blue-900/40 overflow-hidden" style="background: #080e1c;">
          <div class="absolute inset-0 overflow-hidden rounded-xl">
            <div class="absolute" style="top: 50%; left: 50%; transform: translate(-40%, -52%)">
              <WorldGlobe :regions="regions" :model-value="globeRegion" :size="310" />
            </div>
          </div>
          <div
            class="absolute inset-x-0 bottom-0 h-24"
            style="background: linear-gradient(to top, #080e1c 40%, rgba(8,14,28,0.6) 75%, transparent)"
          />
          <p class="relative z-10 text-xs uppercase tracking-wide text-gray-300 px-6 pt-4">{{ $t('play.matchmaking.region') }}</p>
          <p class="absolute bottom-4 left-6 z-10 text-xl font-bold text-white">{{ lobby.region?.name }}</p>
        </div>
      </section>
    </div>
  </DashboardLayout>
</template>

<script setup>
import {
  ref, computed, onMounted, onUnmounted, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/vue/24/outline';
import fallbackAvatar from '../assets/logos/refrag-avatar.svg';
import DashboardLayout from '../components/layouts/DashboardLayout.vue';
import WorldGlobe from '../components/Matchmaking/WorldGlobe.vue';
import RequeueBar from '../components/Matchmaking/RequeueBar.vue';
import LanLobbyBar from '../components/LanParty/LanLobbyBar.vue';
import refragLogo from '../assets/logos/refrag-icon-button.svg';

const { t } = useI18n();
const auth = useAuth();
const user = ref(auth.user());
const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(true);
const error = ref(null);
const copied = ref(false);
const regions = ref([]);
const connectSecondsLeft = ref(0);
let connectTimer = null;
let copiedTimer = null;

const inSteamClient = /Valve Steam/i.test(navigator.userAgent);

const canUseMatchmaking = computed(() => !!auth.user()?.can_use_matchmaking);

const wsLobby = computed(() => store.getters.matchmakingLobby);
const lobby = ref(null);

const IN_PROGRESS_STATUSES = ['forming', 'vetoing', 'waiting_for_server', 'waiting_for_players'];
const statusInProgress = computed(() => IN_PROGRESS_STATUSES.includes(lobby.value?.status));
const connectOpen = computed(() => lobby.value?.status === 'waiting_for_players' && !!lobby.value?.connect_deadline_at);
const serverLive = computed(() => lobby.value?.status === 'active' || connectOpen.value);

const steamConnectUrl = computed(() => {
  const server = lobby.value?.cs_server;
  if (!server) return null;
  const host = server.raw_ip || server.ip;
  const base = `steam://connect/${host}:${server.port}`;
  return server.password ? `${base}/${server.password}` : base;
});

const playlistImage = computed(() => {
  const name = lobby.value?.play_mode?.name;
  return name ? `/img/mods/${name.toLowerCase()}.webp` : null;
});

const mapArt = computed(() => {
  const name = lobby.value?.cs_map?.name;
  return name ? `/maps/${name}_cs2.png` : null;
});
const mapArtFailed = ref(false);

const mapPinFailed = ref(false);

const teams = computed(
  () => lobby.value?.teams || [lobby.value?.team1 || [], lobby.value?.team2 || []],
);

const globeRegion = computed(
  () => regions.value.find((region) => region.name === lobby.value?.region?.name) || null,
);

// Steam IDs a set of lobby members / a match_data side are made of. Both the
// user object and the match_data players use slightly different key names
// depending on the source, so check the known variants.
function memberSteamIds(members) {
  return new Set(
    (members || [])
      .map((m) => String(m?.user?.steam_id ?? m?.user?.steamid ?? m?.user?.steam_id_64 ?? ''))
      .filter(Boolean),
  );
}
function sideSteamIds(side) {
  return new Set(
    (side?.players || [])
      .map((p) => String(p?.steamid ?? p?.steam_id ?? ''))
      .filter(Boolean),
  );
}
function overlapCount(a, b) {
  let n = 0;
  a.forEach((id) => { if (b.has(id)) n += 1; });
  return n;
}

function matchDataTeams(md) {
  if (Array.isArray(md?.teams)) return md.teams;
  if (md?.team_a && md?.team_b) return [md.team_a, md.team_b];
  return [];
}

// match_data teams are not in lobby order (CS sides swap at halftime), so pair them by steam ID
// and only fall back to position for teams nothing matched.
const teamScores = computed(() => {
  const sides = matchDataTeams(lobby.value?.match_data);
  if (!sides.length) return [];

  const sideIds = sides.map(sideSteamIds);
  const used = new Set();
  const paired = teams.value.map((members) => {
    const ids = memberSteamIds(members);
    let best = -1;
    let bestCount = 0;
    sideIds.forEach((side, i) => {
      const count = used.has(i) ? 0 : overlapCount(ids, side);
      if (count > bestCount) {
        best = i;
        bestCount = count;
      }
    });
    if (best !== -1) used.add(best);
    return best;
  });
  const leftover = sides.map((_, i) => i).filter((i) => !used.has(i));
  return paired.map((i) => {
    const side = sides[i === -1 ? leftover.shift() : i];
    return side ? { score: side.score ?? 0 } : null;
  });
});

function teamScore(index) {
  return teamScores.value[index] || null;
}

const playedDate = computed(() => {
  // TODO: swap to a real match-completed timestamp once the backend adds one.
  const raw = lobby.value?.connect_deadline_at;
  if (!raw) return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
});

// Prefer the Refrag username, falling back to the linked Steam persona name.
function memberName(member) {
  return member?.user?.username || member?.user?.personaname || '';
}
function memberAvatar(member) {
  return member?.user?.avatarfull || member?.user?.avatar || fallbackAvatar;
}

const standings = computed(() => {
  const rows = teams.value.map((members, index) => ({
    index,
    label: (members.length === 1 && memberName(members[0])) || t('play.matchmaking.team', { n: index + 1 }),
    score: teamScore(index)?.score ?? 0,
  }));
  return rows
    .map((row) => ({ ...row, rank: 1 + rows.filter((other) => other.score > row.score).length }))
    .sort((a, b) => a.rank - b.rank);
});

const hasScores = computed(() => teamScores.value.some(Boolean));
const showPodium = computed(
  () => lobby.value?.status === 'completed' && hasScores.value && teams.value.length > 2,
);
const placings = computed(() => standings.value.map((row) => ({ ...row, place: row.rank })));
const widePlacings = computed(() => placings.value.length > 4);
const placingAvatarSize = computed(() => (widePlacings.value ? 'w-20 h-20 xl:w-16 xl:h-16' : 'w-20 h-20'));

const PLACE_TEXT = { 1: 'text-yellow-300', 2: 'text-slate-100', 3: 'text-orange-300' };
const PLACE_RING = { 1: 'ring-yellow-400', 2: 'ring-slate-200', 3: 'ring-orange-400' };
function placeText(place) {
  return PLACE_TEXT[place] || 'text-text-secondary';
}
function placeRing(place) {
  return PLACE_RING[place] || 'ring-gray-700';
}

const connectLabel = computed(() => {
  const m = Math.floor(connectSecondsLeft.value / 60);
  const s = String(connectSecondsLeft.value % 60).padStart(2, '0');
  return `${m}:${s}`;
});
const connectUrgent = computed(() => connectSecondsLeft.value <= 30);

const myTeamIndex = computed(() => teams.value.findIndex(
  (team) => team.some((member) => member.user?.uuid && member.user.uuid === user.value?.uuid),
));
const myPlacing = computed(
  () => placings.value.find((row) => row.index === myTeamIndex.value) || null,
);

const userResult = computed(() => {
  if (lobby.value?.status !== 'completed' || !hasScores.value || myTeamIndex.value === -1) return null;
  if (showPodium.value) return myPlacing.value?.rank === 1 ? 'win' : null;
  const mine = teamScore(myTeamIndex.value)?.score ?? 0;
  const theirs = teamScore(1 - myTeamIndex.value)?.score ?? 0;
  if (mine === theirs) return 'tie';
  return mine > theirs ? 'win' : 'loss';
});

function scoreColor(index) {
  if (userResult.value !== 'win' && userResult.value !== 'loss') return '';
  const higher = (teamScore(index)?.score ?? 0) > (teamScore(1 - index)?.score ?? 0);
  if (!higher) return '';
  return userResult.value === 'win' ? 'text-success-400' : 'text-danger-400';
}

function mirrored(index) {
  return teams.value.length === 2 && index === 1;
}

const TEAM_HOME = { border: 'border-primary-700/60', dot: 'bg-primary-500', avatar: 'bg-primary-700' };
const TEAM_AWAY = { border: 'border-orange-700/60', dot: 'bg-orange-500', avatar: 'bg-orange-700' };
const TEAM_NEUTRAL = { border: 'border-gray-800', dot: 'bg-gray-500', avatar: 'bg-gray-700' };
function teamAccent(index) {
  if (teams.value.length !== 2) return TEAM_NEUTRAL;
  return index === 0 ? TEAM_HOME : TEAM_AWAY;
}

const CHIP_PENDING = 'bg-[#3B82F633] text-blue-400';
const CHIP_GOOD = 'bg-[#12928333] text-success-400';
const CHIP_BAD = 'bg-[#8B000033] text-danger-300';
const CHIP_NEUTRAL = 'bg-gray-800 text-text-secondary';

const statusChip = computed(() => {
  switch (lobby.value?.status) {
    case 'forming': return { label: t('play.matchmaking.chip_accepting'), class: CHIP_PENDING, pulse: true };
    case 'vetoing': return { label: t('play.matchmaking.chip_veto'), class: CHIP_PENDING, pulse: true };
    case 'waiting_for_server': return { label: t('play.matchmaking.chip_starting'), class: CHIP_PENDING, pulse: true };
    case 'waiting_for_players':
      if (!connectOpen.value) return { label: t('play.matchmaking.chip_starting'), class: CHIP_PENDING, pulse: true };
      return { label: t('play.matchmaking.chip_waiting'), class: CHIP_PENDING, pulse: true };
    case 'active': return { label: t('play.matchmaking.chip_live'), class: 'bg-red-500/20 text-red-400', pulse: true };
    case 'cancelled': return { label: t('play.matchmaking.chip_cancelled'), class: CHIP_BAD };
    case 'completed':
      if (userResult.value === 'win') return { label: t('coach.match_header.victory'), class: CHIP_GOOD };
      if (userResult.value === 'loss') return { label: t('coach.match_header.defeat'), class: CHIP_BAD };
      if (userResult.value === 'tie') return { label: t('coach.match_header.tie'), class: CHIP_BAD };
      return { label: t('play.matchmaking.chip_finished'), class: CHIP_NEUTRAL };
    default: return { label: lobby.value?.status ?? '', class: CHIP_NEUTRAL };
  }
});

const heroCaption = computed(() => {
  const status = lobby.value?.status;
  if (status === 'completed') return playedDate.value;
  if (status === 'cancelled') return t('play.matchmaking.no_server_cancelled');
  if (status === 'active') return t('play.matchmaking.status_active');
  if (status === 'waiting_for_players' && connectOpen.value) {
    const members = teams.value.flat();
    return t('play.matchmaking.connected_count', { n: members.filter((m) => m.connected).length, total: members.length });
  }
  return '';
});

function copyConnectCommand() {
  const server = lobby.value?.cs_server;
  if (!server) return;
  const connect = `connect ${server.ip}:${server.port}`;
  const textToCopy = server.password ? `${connect}; password ${server.password}` : connect;
  navigator.clipboard?.writeText(textToCopy).then(() => {
    copied.value = true;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => (copied.value = false), 1200);
  });
}

function startConnectCountdown(deadlineIso) {
  clearInterval(connectTimer);
  const tick = () => {
    const ms = new Date(deadlineIso) - Date.now();
    connectSecondsLeft.value = Math.max(0, Math.round(ms / 1000));
  };
  tick();
  connectTimer = setInterval(tick, 1000);
}

function applyLobby(data) {
  lobby.value = data;
  if (data.status === 'waiting_for_players' && data.connect_deadline_at) {
    startConnectCountdown(data.connect_deadline_at);
  } else {
    clearInterval(connectTimer);
  }
}

function loadLobby() {
  const { id } = route.params;
  return axios
    .get(`/matchmaking/lobbies/${id}`)
    .then(({ data }) => {
      if (id === route.params.id) applyLobby(data);
    })
    .catch(() => {
      if (id === route.params.id) error.value = t('play.matchmaking.lobby_not_found');
    });
}

async function showLobby() {
  loading.value = true;
  error.value = null;
  lobby.value = null;
  mapArtFailed.value = false;
  mapPinFailed.value = false;
  if (wsLobby.value?.uuid == route.params.id) {
    applyLobby(wsLobby.value);
  } else {
    await loadLobby();
  }
  loading.value = false;
}

onMounted(async () => {
  if (!canUseMatchmaking.value) {
    await auth.fetch().catch(() => {});
    if (!canUseMatchmaking.value) {
      router.push('/');
      return;
    }
  }

  axios.get('/regions')
    .then(({ data }) => { regions.value = data.regions; })
    .catch(() => {});

  await showLobby();
});

watch(() => route.params.id, (id, previousId) => {
  if (id && id !== previousId) showLobby();
});

onUnmounted(() => {
  clearInterval(connectTimer);
  clearTimeout(copiedTimer);
});

watch(wsLobby, (updated) => {
  if (updated?.uuid == route.params.id) {
    applyLobby(updated);
  }
});
</script>

<style scoped>
.podium-1 {
  background: radial-gradient(90% 65% at 50% 0%, rgba(250, 204, 21, 0.38) 0%, rgba(250, 204, 21, 0) 100%), #17140a;
  box-shadow: inset 0 0 0 1px rgba(250, 204, 21, 0.5);
}

.podium-2 {
  background: radial-gradient(90% 65% at 50% 0%, rgba(241, 245, 249, 0.34) 0%, rgba(241, 245, 249, 0) 100%), #17191d;
  box-shadow: inset 0 0 0 1px rgba(241, 245, 249, 0.5);
}

.podium-3 {
  background: radial-gradient(90% 65% at 50% 0%, rgba(205, 127, 50, 0.38) 0%, rgba(205, 127, 50, 0) 100%), #17100a;
  box-shadow: inset 0 0 0 1px rgba(205, 127, 50, 0.5);
}

.seat-mine {
  background: radial-gradient(90% 65% at 50% 0%, rgba(78, 84, 200, 0.4) 0%, rgba(78, 84, 200, 0) 100%), #08091b;
}

.podium-rest {
  background: radial-gradient(90% 65% at 50% 0%, rgba(124, 147, 177, 0.1) 0%, rgba(124, 147, 177, 0) 100%), #0f141a;
}

.faceoff-home {
  background: radial-gradient(70% 100% at 0% 50%, rgba(78, 84, 200, 0.1) 0%, rgba(78, 84, 200, 0) 100%);
}

.faceoff-away {
  background: radial-gradient(70% 100% at 100% 50%, rgba(194, 65, 12, 0.1) 0%, rgba(194, 65, 12, 0) 100%);
}

.map-art {
  opacity: 0.4;
  -webkit-mask-image:
    linear-gradient(to bottom, transparent 0%, #000 30%, #000 60%, transparent 100%),
    linear-gradient(to right, transparent 0%, #000 18%, #000 82%, transparent 100%);
  -webkit-mask-composite: source-in;
  mask-image:
    linear-gradient(to bottom, transparent 0%, #000 30%, #000 60%, transparent 100%),
    linear-gradient(to right, transparent 0%, #000 18%, #000 82%, transparent 100%);
  mask-composite: intersect;
}

.card-gradient {
  background: linear-gradient(180deg, rgba(15, 19, 26, 0.85) 0%, rgba(15, 19, 26, 0.35) 45%, rgba(15, 19, 26, 0.9) 100%);
}
</style>
